<template>
    <div    class="flex flex-col">
        <div>
            <h2>Create Device Profile Template</h2>
        </div>

        <!-- Profile Section -->
        <div    class="">
            <div    class="flex flex-col lg:flex-row lg:justify-between">
                <!-- Column 1 to determine if profile is active -->
                <div    class="w-full lg:w-1/2 flex flex-col justify-start items-center">
                    <div    class="mt-6 relative z-0 flex shadow-sm rounded-md shrink"
                            @click="profileActive = !profileActive">
                        <button     type="button"
                                    class="-ml-px relative inline-flex items-center px-4 py-2 border rounded-l-md bg-white text-sm font-medium hover:bg-gray-50"
                                    :class="[profileActive ? 'border-blue-500 text-gray-700 z-10' : 'border-gray-300 text-gray-500']"
                        >
                            Active
                        </button>
                        <button     type="button"
                                    class="-ml-px relative inline-flex border-4 items-center px-4 py-2 border rounded-r-md bg-white text-sm font-medium hover:bg-gray-50"
                                    :class="[!profileActive ? 'border-blue-500 text-gray-700 z-10' : 'border-gray-300 text-gray-500']"
                        >
                            Inactive
                        </button>
                    </div>

                    <div    class="w-2/3 mt-4">
                        Target Device
                    </div>
                    <div    class="w-2/3 mt-1">
                        <select2    name="device_type_select"
                                    placeholder="Device Type"
                                    :options="deviceTypes"
                                    :close-on-select="true"
                                    v-model:value="targetDeviceType"
                        ></select2>
                    </div>
                </div>

                <!-- Column 2 to provide basic info -->
                <div    class="w-full lg:w-1/2 my-2 flex flex-col items-center">
                    <!-- Template Name -->
                    <div    class="w-full my-2">
                        <floating-input     label="Profile Template Name"
                                            v-model:value="profileName"
                                            id="profile_name"
                        ></floating-input>
                    </div>

                    <!-- Company Select -->
                    <div    class="w-full">
                        <span>Company</span>
                        <select2    :options="companies"
                                    v-model:value="profileCompany"
                                    :close-on-select="true"
                                    name="company_select"
                        ></select2>
                    </div>

                    <!-- Template Description -->
                    <div    class="w-full my-2 flex flex-col items-start">
                        <label  for="template_description">Profile Template Description</label>
                        <textarea   id="template_description"
                                    name="template_description"
                                    v-model="profileDesc"
                                    class="w-full border rounded-md border-4 border-gray-300"
                        >

                        </textarea>
                    </div>
                </div>
            </div>

            <!-- Actual Characteristics for profile template -->
            <div    class="">
                <div    class="flex justify-end items-center">
                    <div    class="w-1/5">
                        <span   v-if="errors.length > 0"
                                class="text-red-700">
                            Fill out required pieces
                        </span>
                        <span   v-else-if="!canSave"
                                class="text-red-700">
                            Cannot Save Until Above info is Set
                        </span>
                    </div>
                </div>
                <div    class="flex w-full justify-end items-center ">
                    <ul class="">
                        <li     v-for="error in errors">
                            - {{ error }}
                        </li>
                    </ul>
                </div>
                <!-- Table -->
                <ajax-table     :data="data"
                                :headers="headers"
                                v-model:loading="ajaxTable.loading"
                                v-model:search="ajaxTable.search"
                                v-model:sort-by="ajaxTable.sortBy"
                                v-model:sort-desc="ajaxTable.sortDesc"
                                v-model:page="ajaxTable.page"
                                v-model:items-per-page="ajaxTable.itemsPerPage"
                                v-model:page-info="ajaxTable.pageInfo"
                                :exportable="false"
                                @loadData="sortRows"
                >
                    <!-- Start Disabling items-per-page & pagination by using empty slots -->
                    <template   #items-per-page>
                        <div    class="flex items-center justify-between">
                            <div    class="">
                                <green-button
                                    @click="saveProfile"
                                >
                                    <span   v-if="saving">Saving</span>
                                    <span   v-else-if="canSave">Save Profile</span>
                                    <span   v-else>More Info Needed</span>
                                </green-button>
                            </div>
                        </div>
                    </template>
                    <template   #pagination>
                        <div></div>
                    </template>

                    <!-- Should Include Parameter -->
                    <template   #col-1="props">
                        <div    class="w-full h-full flex items-center justify-start ml-8">
                            <small-colored-check    v-model="data[props.rowIndex]['include']"
                                                    :togglable="true"
                            ></small-colored-check>
                        </div>
                    </template>

                    <!-- Parameter Name -->
                    <template   #col-2="props">
                        <div    class="flex flex-col items-start">
                            <span   class="my-1 text-gray-900">
                                {{ props.row.displayName }}
                            </span>
                            <span   class="my-1 text-gray-500">
                                {{ props.row.name }}
                            </span>
                            <span   class="my-1 text-gray-500">
                                {{ props.row.shortUUID }}
                            </span>
                        </div>
                    </template>

                    <!-- Value -->
                    <template   #col-3="props">
                        <div    class="w-full flex flex-col"
                                >
                            <span   class="inline lg:hidden">{{ props.row.displayName }}</span>
                            <div    v-if="props.row.editType === 'select'"
                                    :class="[props.row.value !== props.row.default && !props.row.include ? 'border border-red-400 rounded-md' : '']">
                                <select2    :name="props.row.name + '_param'"
                                            :placeholder="props.row.format"
                                            :options="props.row.editSelectOptions"
                                            :close-on-select="true"
                                            :tags="props.row.allowSelectTags"
                                            v-model:value="data[props.rowIndex]['value']"
                                ></select2>
                            </div>
                            <div    v-else>
                                <floating-input     :label="props.row.format"
                                                    v-model:value="data[props.rowIndex]['value']"
                                                    :class="[props.row.value !== '' && !props.row.include ? 'border border-red-400' : '']"
                                                    :id="props.row.uuid"
                                ></floating-input>
                            </div>
                        </div>
                    </template>

                </ajax-table>
            </div>
        </div>

    </div>
</template>

<script>

import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import GreenButton from "@/components/elements/green-button";
import Select2 from "@/components/elements/select2"
import Toggle from "@/components/elements/toggle";
import FloatingInput from "@/components/elements/FloatingInput";
import AjaxTable from "@/components/elements/AjaxTable";
import SmallColoredCheck from "@/components/elements/SmallColoredCheck";
import { debounce, isNil, isEmpty } from "lodash"

const DEBOUNCED_GET_INTERVAL = 200;

export default {
    name: "DeviceProfileTemplateCreate",
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin],
    components: {SmallColoredCheck, FloatingInput, Toggle, GreenButton, Select2, AjaxTable},
    props: {

    },
    data() {
        return {
            debouncedGetFunction: null,
            request: 0,
            data: [],
            hiddenData: [],
            companies: {},
            headers: [
                {
                    name: 'Include',
                    sortBy: '',
                    show: 'sm'
                },
                {
                    name: 'Parameter',
                    sortBy: '',
                    show: 'lg',
                },
                {
                    name: 'Value',
                    sortBy: '',
                    show: 'sm',
                },
            ],
            deviceTypes: {},

            // Properties of the profile
            duplicateProfileId: null,
            profileName: '',
            profileDesc: '',
            profileActive: false,
            profileCompany: null,
            targetDeviceType: null,


            // Error / Save Handling
            saving: false,
            errors: [],
        }
    },
    created() {
        // No Paging - Ever
        this.ajaxTable.itemsPerPage = "1000"
        this.duplicateProfileId = sessionStorage.getItem('duplicateProfile')
        sessionStorage.removeItem('duplicateProfile')

        // Debounced General Request Function to get the device profile template parameters and info.
        this.debouncedGetFunction = debounce(() => {
            this.ajaxTable.loading = true
            this.request++

            let company = this.profileCompany
            if (isNil(company) || isEmpty(company)) {
                // FYI: We can't call getCompanyId before the debounce function without causing errors in Vue3
                company = this.company.getCompanyId()
            }
            axios.get(this.company.buildCompanyApiInternalRoute('internal-device-profile-template/create', company), {
                params: {
                    deviceType: this.targetDeviceType,
                    requestNumber: this.request,
                    duplicateProfile: this.duplicateProfileId,
                }
            }).then(response => {
                let data = response.data
                if (response.data.requestNumber !== `${this.request}`) {
                    return
                }

                this.data = data.data
                this.companies = data.companies
                this.profileName = data.name
                this.profileDesc = data.desc
                this.profileActive = data.active
                this.profileCompany = data.company

                this.deviceTypes = data.deviceTypes
                this.targetDeviceType = data.deviceType
            }).catch(error => {

            }).then(() => {
                this.ajaxTable.loading = false
            })
        }, DEBOUNCED_GET_INTERVAL)
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.debouncedGetFunction()
        },
        sortRows() {
            let search = this.ajaxTable.search.toLowerCase()
            this.hiddenData.forEach(row => {
                this.data.push(row)
            })
            this.hiddenData = []

            let index = 0
            while (index < this.data.length) {
                let parameter = this.data[index]
                let found = false
                found = found || parameter.displayName.toLowerCase().indexOf(search) !== -1
                found = found || parameter.name.toLowerCase().indexOf(search) !== -1
                found = found || parameter.uuid.toLowerCase().indexOf(search) !== -1

                if (!found) {
                    this.hiddenData.push(this.data[index])
                    this.data.splice(index, 1)
                    continue
                }

                index++
            }

            this.data.sort((a, b) => {
                return  parseInt(a.uuid, 16) - parseInt(b.uuid, 16)
            })
        },
        saveProfile() {
            this.updateUiError()
            if (this.saving || !this.canSave || this.errors.length > 0) {
                return
            }
            this.saving = true
            axios.post(this.company.buildCompanyApiInternalRoute('internal-device-profile-template'), {
                name: this.profileName,
                description: this.profileDesc,
                active: this.profileActive,
                deviceType: this.targetDeviceType,
                company_id: this.profileCompany,
                characteristics: this.data,
            }).then(response => {
                window.location = this.company.buildCompanyRoute('device-profile-template')
            }).catch(error => {
                this.errors.push('Error Saving your Profile.  Please check the values again.')
            }).then(() => {
                this.saving = false
            })
        },
        updateUiError() {
            this.errors = []
            if (this.profileName === '') {
                this.errors.push('Must enter a name for the profile.')
            }
            if (this.profileDesc === '') {
                this.errors.push('Must enter a description for the profile.')
            }
            if (this.profileCompany === '') {
                this.errors.push('Must select a company for the profile to live on.')
            }
            if (!this.atLeastOneCharacteristicIsIncluded) {
                this.errors.push('Must include at least one Device Characteristic.')
            }
        },
    },
    computed: {
        canSave() {
            return this.profileCompany !== '' &&
                   this.profileName !== '' &&
                   this.profileDesc !== '' &&
                   this.atLeastOneCharacteristicIsIncluded
        },
        atLeastOneCharacteristicIsIncluded() {
            let included = false
            this.data.forEach(item => {
                included = included || item.include
            })

            return included
        },
    },
    watch: {
        targetDeviceType(val, old) {
            if (val !== old) {
                this.getData()
            }
        },
        profileCompany(val, old) {
            if (!_.isNil(this.profileCompany) && !_.isNil(old) && this.profileCompany !== old) {
                this.getData()
            }
        },
    },
}

</script>

<style scoped>

</style>